import { useState, useMemo } from 'react'
import Box from '@components/base/Box'
import Text from '@components/base/Text'
import { TextInput } from '@components/inputs/TextInput'
import { useTheme } from '@libs/useTheme'
import type {
  Event,
  EventDetails,
  SubscriptionOrder,
  User,
} from '@graphql/__generated/graphql'
import { v4 } from 'uuid'
import { useAuth } from '@libs/Auth/useAuth'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { GET_USERS_BY_IDS } from '@graphql/users/GetUsersByIds'
import { GET_EVENT_BY_ID } from '@graphql/events/GetEventById'
import LoadingSpinner from '@components/base/LoadingSpinner'
import { GET_EVENTS_BY_IDS } from '@graphql/events/GetEventsByIds'
import OrderModal from '@components/order-manager/OrderModal'
import { UPDATE_ORDER } from '@graphql/events/updateOrder'
import { GET_ACTIVE_MEMBERSHIPS_BY_COMPANY } from '@graphql/events/GetMembershipsByCompany'
import MemberModal from '@components/order-manager/MemberModal'
import moment from 'moment'

export type MemberData = SubscriptionOrder & {
  orderId?: string
  user?: Partial<User>
}

const getFiltered = (
  orders: (SubscriptionOrder | null | undefined)[],
  filter: string
) => {
  if (!filter || !orders) return orders
  const lowercasedSearchText = filter.toLowerCase()

  const payload = orders.filter((order) => {
    const orderEmail = order?.customerEmail

    return (
      orderEmail?.includes(lowercasedSearchText) ||
      order?.orderId.includes(lowercasedSearchText) ||
      order?.userId?.includes(lowercasedSearchText)
    )
  })
  return payload
}

const TableMembers = ({
  orders,
  className,
  refetch,
}: {
  orders: SubscriptionOrder[]
  refetch: () => void
  className?: string
}) => {
  const { profile } = useAuth()
  const { theme } = useTheme()
  const isDarkTheme = theme.type === 'DARK'

  const org = profile?.company ?? profile?.university!

  const [filter, setFilter] = useState<string>('')
  const [users, setUsers] = useState<User[]>([])
  // const [memberData, setMemberData] = useState<SubscriptionOrder[]>([])

  const orderUsers = orders?.map((o) => o?.userId).filter((o) => !!o)

  // const { loading: membersLoading } = useQuery(
  //   GET_ACTIVE_MEMBERSHIPS_BY_COMPANY,
  //   {
  //     variables: { organisationName: profile?.company! },
  //     skip: !profile?.company,
  //     onCompleted: (data) => {
  //       const payload = data.getActiveMembershipsByCompany
  //       setMemberData(payload)
  //     },
  //   }
  // )

  const { loading: usersLoading } = useQuery<{
    getUsersByIds: { items: User[] }
  }>(GET_USERS_BY_IDS, {
    variables: { input: { userIds: orderUsers as string[] } },
    skip: orderUsers.length < 1,
    onCompleted: (data) => {
      setUsers(data.getUsersByIds.items)
    },
  })

  const filteredData = useMemo(() => {
    return getFiltered(orders, filter).map((o) => {
      const user = users.find((u) => u.userId === o?.userId)
      // const event = eventsData.find((e) => e.eventId === o?.eventId)
      const payload = {
        ...o,
        user,
      } as MemberData
      return payload
    })
  }, [orders, filter, users])

  const [isOrderModalOpen, setOrderModalOpen] = useState(false)
  const [orderModalData, setOrderModalData] = useState<MemberData>()

  const [updateOrder] = useMutation(UPDATE_ORDER)

  const handleSubmit = (data: MemberData) => {
    if (data.orderId) {
      updateOrder({
        variables: {
          input: {
            orderId: data.orderId,
            paid: data.paid,
          },
        },
      }).then(() => refetch())
    }
  }

  return (
    <>
      <MemberModal
        show={isOrderModalOpen}
        setShow={setOrderModalOpen}
        className={`max-w-md`}
        title={'Member Manager'}
        data={orderModalData}
        onComplete={(data) => handleSubmit(data)}
      />
      <Box className="w-full flex justify-center items-center">
        <Box
          color="foregroundColour"
          className={`flex-2 rounded-3xl border shadow-md border-forumm-menu-border p-3 sm:p-5 max-w-[1500px] justify-center items-center ${className}`}
        >
          <Box className="flex flex-col md:flex-row w-full justify-between items-center mb-8">
            <Text className="text-xl text-center md:text-left">{`${org}'s Members`}</Text>
            <Box className="mt-4 md:mt-0 md:min-w-[300px]">
              <TextInput
                value={filter}
                className="w-full"
                placeholder="Enter your search terms"
                onChange={setFilter}
              />
            </Box>
          </Box>
          <Box className="max-h-[300px] overflow-y-scroll scrollbar-hide">
            <Box
              className={`w-full flex flex-row ${
                isDarkTheme ? 'bg-dark' : 'bg-forumm-light-gray'
              } rounded-t-xl py-2 mb-5`}
            >
              <Box className="flex-3 font-bold text-sm md:text-base">
                User Email
              </Box>
              <Box className="flex-3 font-bold text-start text-sm sm:text-base ml-4">
                Order ID
              </Box>
              <Box className="flex-1 text-center font-bold text-sm md:text-base">
                Active
              </Box>
              <Box className="flex-1 text-center font-bold text-sm md:text-base">
                Last Paid
              </Box>
            </Box>
            {filteredData.map((orderData, index) => {
              return (
                <Box
                  key={orderData.orderId}
                  className={`flex flex-row items-center cursor-pointer ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                  onClick={() => {
                    setOrderModalOpen(true)
                    setOrderModalData(orderData)
                  }}
                >
                  <Box className="flex-3 text-sm md:text-base break-all p-2">
                    {orderData.customerEmail}
                  </Box>
                  <Box className="flex-3 flex flex-row text-start justify-start text-sm md:text-base ml-4 break-all p-2">
                    {orderData?.orderId}
                  </Box>
                  <Box
                    className={`flex-1 text-center text-sm md:text-base ${orderData?.paid ? 'border border-green-300' : 'border border-yellow-300'} rounded-xl`}
                  >
                    {orderData?.paid ? 'ACTIVE' : 'INACTIVE'}
                  </Box>
                  <Box className="flex-1 text-center text-sm md:text-base">
                    {orderData.lastUpdated
                      ? moment(orderData?.lastUpdated).format('DD/MM/YYYY')
                      : ''}
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default TableMembers
